import React, { useState, useEffect, Suspense } from 'react';
import { getToothComponent } from './tooth';

interface ToothSelectorProps {
  teethNumbers: number[];
  selectable?: boolean;
  onTeethChange?: (teethNumbers: number[]) => void;
}

export default function ToothSelector({ teethNumbers, selectable = false, onTeethChange }: ToothSelectorProps) {
  // Initialize state with the passed teethNumbers
  const [selectedTeeth, setSelectedTeeth] = useState<number[]>(teethNumbers);

  // Update selectedTeeth if teethNumbers prop changes
  useEffect(() => {
    setSelectedTeeth(teethNumbers);
  }, [teethNumbers]);

  const handleToothClick = (toothNumber: number) => {
    if (!selectable) return;
    
    const newSelectedTeeth = selectedTeeth.includes(toothNumber)
      ? selectedTeeth.filter(t => t !== toothNumber)
      : [...selectedTeeth, toothNumber];
    
    setSelectedTeeth(newSelectedTeeth);
    onTeethChange?.(newSelectedTeeth);
  };

  const renderToothRow = (
    start: number,
    end: number,
    reverse = false,
    flipLayout = false
  ) => {
    const numbers = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    const teeth = reverse ? numbers.reverse() : numbers;
    
    return (
      <div className={`flex flex-wrap justify-center ${selectable ? 'gap-3' : 'gap-2'}`}>
        {teeth.map((tooth) => {
          const isSelected = selectedTeeth.includes(tooth);
          const ToothComponent = getToothComponent(tooth);

          return (
            <div
              key={tooth}
              className={`flex items-center text-center ${flipLayout ? 'flex-col' : 'flex-col-reverse'}`}
            >
              <div
                className={selectable ? 'cursor-pointer' : ''}
                onClick={() => handleToothClick(tooth)}
                onKeyDown={(e) => {
                  if (selectable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleToothClick(tooth);
                  }
                }}
                tabIndex={selectable ? 0 : -1}
                role={selectable ? 'button' : undefined}
                aria-label={selectable ? `${isSelected ? 'Deselect' : 'Select'} tooth ${tooth}` : undefined}
              >
                <Suspense fallback={<div className={`bg-gray-200 rounded animate-pulse ${selectable ? 'w-6 h-[3.85rem]' : 'w-[clamp(0.75rem,calc(50vw-48.5rem),1.5rem)] h-[clamp(2.125rem,calc(50vw-48.5rem),3.725rem)]'}`} />}>
                  <ToothComponent
                    isSelected={isSelected}
                    className={`object-contain transition-colors ${selectable ? 'h-[3.85rem]' : 'h-[clamp(2.125rem,calc(50vw-48.5rem),3.725rem)]'}`}
                    color={selectable ? '#55B494' : '#5B73F1'}
                  />
                </Suspense>
              </div>
              <p className={`text-[#21272A] font-bold ${selectable ? 'text-xs' : 'text-[clamp(0.53125rem,calc(50vw-48.5rem),0.75rem)]'}`}>
                {tooth}
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center py-2">
      {renderToothRow(1, 16)}
      <div className="flex justify-center mt-5 mb-3 w-[95%] border-t border-t-black" />
      {renderToothRow(17, 32, true, true)}
    </div>
  );
}
